/* eslint-disable react-hooks/immutability */
//
// The React Compiler rules assume a value held across renders is application
// state. The value here is a DOM media element, and the two things this file
// does with it are read its clock and move its playhead. Neither has a setter,
// and neither is state in the sense the rules mean.
//
// Restructured twice trying to satisfy them (ref, then state) before deciding
// they are wrong about this particular case rather than the code being wrong.
import { useEffect, useRef, useState } from "react";
import type { AssetManifest } from "@scenix/core";
import { timecode } from "../lib/scrub";

/**
 * The transcript, wired to the player.
 *
 * Clicking a line seeks to it and the line under the playhead stays
 * highlighted, which is the whole reason to have a transcript beside a video
 * rather than under it: reading is how you find the moment, and the moment is
 * what you actually want.
 *
 * Given the time and a way to seek rather than the element itself, so this
 * never reaches into something it does not own.
 */
export function Transcript({
  manifest,
  at,
  seek,
  following,
  onFollowingChange,
}: {
  manifest: AssetManifest;
  at: number;
  seek: (seconds: number) => void;
  following: boolean;
  onFollowingChange: (following: boolean) => void;
}) {
  const active = useRef<HTMLButtonElement>(null);
  const segments = manifest.transcript?.segments ?? [];
  const current = segments.findIndex((s) => at >= s.startSec && at < s.endSec);

  useEffect(() => {
    // Only while following. Scrolling away to read ahead should not be undone
    // by playback dragging the view back every quarter second.
    if (following && current >= 0) {
      active.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [current, following]);

  if (segments.length === 0) return null;

  return (
    <>
      <div className="section-bar">
        <h2 className="section-head">Transcript</h2>
        <label className="follow">
          <input
            type="checkbox"
            checked={following}
            onChange={(event) => onFollowingChange(event.target.checked)}
          />
          Follow playback
        </label>
      </div>

      <div className="transcript-lines">
        {segments.map((segment, i) => (
          <button
            key={`${segment.startSec}-${i}`}
            type="button"
            ref={i === current ? active : undefined}
            className={`line${i === current ? " is-current" : ""}`}
            onClick={() => seek(segment.startSec)}
            title={`Jump to ${timecode(segment.startSec)}`}
          >
            <span className="line-at">{timecode(segment.startSec)}</span>
            {segment.speaker && <span className="line-speaker">{segment.speaker}</span>}
            <span className="line-text">{segment.text}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * Owns the media element and reports where playback is.
 *
 * Kept here rather than in the page so the transcript and anything else that
 * wants to follow along share one source of truth about the time.
 */
export function usePlayer() {
  // The element is held as state rather than a ref, so the effect below runs
  // when it mounts and nothing reads a ref during render.
  const [element, setElement] = useState<HTMLMediaElement | null>(null);
  const [at, setAt] = useState(0);
  const [following, setFollowing] = useState(true);

  useEffect(() => {
    if (!element) return;

    // timeupdate fires about four times a second, which is enough to keep a
    // line highlighted and cheap enough not to matter.
    const onTime = () => setAt(element.currentTime);
    element.addEventListener("timeupdate", onTime);
    element.addEventListener("seeked", onTime);
    return () => {
      element.removeEventListener("timeupdate", onTime);
      element.removeEventListener("seeked", onTime);
    };
  }, [element]);

  const seek = (seconds: number) => {
    if (!element) return;
    element.currentTime = seconds;
    setFollowing(true);
    void element.play().catch(() => {
      // Autoplay can be refused. The seek still happened, which is the part
      // that matters.
    });
  };

  return { attach: setElement, element, at, seek, following, setFollowing };
}
